import { scheduledDark, MONITOR_PRESETS, MONITOR_TILES, MONITOR_TILE_KEYS, type MonitorInput, type MonitorTile, type MonitorTileKey } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Copy, ExternalLink, MonitorPlay, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MonitorGrid, type TileData } from '@/components/monitor-tiles';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Select, Switch, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import type { AreaConfig } from '@/lib/types';
import { cn, formatDateTime } from '@/lib/utils';

interface Monitor extends MonitorInput {
  id: string;
  token: string;
  lastViewedAt: string | null;
  updatedAt: string;
}
interface Edit {
  id: string | null;
  form: MonitorInput;
  networks: string;
}

const onError = (e: unknown) => toast.error(errorMessage(e));
const newId = () => Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => (b % 36).toString(36)).join('');
const urlOf = (token: string) => `${window.location.origin}/monitor/${token}`;
const GROUPS = ['Allgemein', 'Tickets', 'Reparaturen', 'Kalender', 'Ziele', 'Abrechnung', 'Monitoring'] as const;

const emptyForm = (preset?: keyof typeof MONITOR_PRESETS): MonitorInput => ({
  name: preset ? MONITOR_PRESETS[preset].name : '',
  columns: 4,
  refreshSeconds: 60,
  theme: 'dark',
  darkFrom: '18:00',
  darkUntil: '07:00',
  areaIds: [],
  allowedNetworks: [],
  enabled: true,
  tiles: preset ? MONITOR_PRESETS[preset].tiles.map((t) => ({ ...t, id: newId() })) : [],
});

function TileRow({ tile, index, count, columns, onChange, onMove, onRemove }: { tile: MonitorTile; index: number; count: number; columns: number; onChange: (t: MonitorTile) => void; onMove: (d: -1 | 1) => void; onRemove: () => void }) {
  const def = MONITOR_TILES[tile.key];
  const opts = def.options as readonly string[];
  const set = (o: Partial<MonitorTile['options']>) => onChange({ ...tile, options: { ...tile.options, ...o } });
  return (
    <div className="grid gap-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-medium">
          {def.label} <span className="font-normal text-muted-foreground">· {def.group}</span>
        </p>
        <div className="flex shrink-0 gap-1">
          <Button type="button" variant="ghost" size="icon" className="size-7" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Nach oben">
            <ArrowUp className="size-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="size-7" disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Nach unten">
            <ArrowDown className="size-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="size-7 text-destructive hover:text-destructive" onClick={onRemove} aria-label={`${def.label} entfernen`}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Input value={tile.title ?? ''} onChange={(e) => onChange({ ...tile, title: e.target.value || null })} placeholder={def.label} aria-label="Titel" className="h-8 w-44" />
        <Select value={tile.w} onChange={(e) => onChange({ ...tile, w: Number(e.target.value) })} aria-label="Breite" className="h-8 w-auto">
          {Array.from({ length: columns }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n} breit
            </option>
          ))}
        </Select>
        <Select value={tile.h} onChange={(e) => onChange({ ...tile, h: Number(e.target.value) })} aria-label="Höhe" className="h-8 w-auto">
          {[1, 2, 3, 4].map((n) => (
            <option key={n} value={n}>
              {n} hoch
            </option>
          ))}
        </Select>
        {opts.includes('limit') && (
          <Select value={tile.options.limit ?? 10} onChange={(e) => set({ limit: Number(e.target.value) })} aria-label="Anzahl Einträge" className="h-8 w-auto">
            {[5, 8, 10, 12, 15, 20, 30].map((n) => (
              <option key={n} value={n}>
                {n} Einträge
              </option>
            ))}
          </Select>
        )}
        {opts.includes('days') && (
          <Select value={tile.options.days ?? 14} onChange={(e) => set({ days: Number(e.target.value) })} aria-label="Zeitraum" className="h-8 w-auto">
            {[7, 14, 21, 30].map((n) => (
              <option key={n} value={n}>
                {n} Tage
              </option>
            ))}
          </Select>
        )}
        {opts.includes('chart') && (
          <Select value={tile.options.chart ?? ''} onChange={(e) => set({ chart: (e.target.value || undefined) as MonitorTile['options']['chart'] })} aria-label="Darstellung" className="h-8 w-auto">
            <option value="">Darstellung: Standard</option>
            <option value="bars">Balken</option>
            <option value="columns">Säulen</option>
            <option value="donut">Ring</option>
          </Select>
        )}
        {opts.includes('seriesStyle') && (
          <Select value={tile.options.seriesStyle ?? 'columns'} onChange={(e) => set({ seriesStyle: e.target.value as 'columns' | 'line' })} aria-label="Verlauf als" className="h-8 w-auto">
            <option value="columns">Säulen</option>
            <option value="line">Linie</option>
          </Select>
        )}
        {opts.includes('target') && (
          <label className="flex items-center gap-1">
            Ziel
            <Input type="number" min={1} max={10000} value={tile.options.target ?? 10} onChange={(e) => set({ target: Math.max(1, Number(e.target.value) || 1) })} className="h-8 w-20" aria-label="Ziel" />
          </label>
        )}
        {opts.includes('showCustomer') && (
          <label className="flex items-center gap-2">
            <Checkbox checked={tile.options.showCustomer ?? false} onCheckedChange={(v) => set({ showCustomer: v === true })} /> Kundennamen anzeigen
          </label>
        )}
      </div>
      {opts.includes('text') && <Textarea rows={2} value={tile.options.text ?? ''} onChange={(e) => set({ text: e.target.value })} placeholder="Text, z. B. Öffnungszeiten" aria-label="Hinweistext" />}
    </div>
  );
}

function Editor({ edit, areas, onClose }: { edit: Edit; areas: AreaConfig[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(edit.form);
  const [networks, setNetworks] = useState(edit.networks);
  const [adding, setAdding] = useState<MonitorTileKey | ''>('');
  const set = <K extends keyof MonitorInput>(k: K, v: MonitorInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setTiles = (fn: (t: MonitorTile[]) => MonitorTile[]) => setForm((f) => ({ ...f, tiles: fn(f.tiles) }));

  // Vorschau mit echten Daten (verzögert, damit nicht jede Eingabe eine Abfrage auslöst)
  const [previewKey, setPreviewKey] = useState(() => JSON.stringify({ areaIds: form.areaIds, tiles: form.tiles }));
  useEffect(() => {
    const t = setTimeout(() => setPreviewKey(JSON.stringify({ areaIds: form.areaIds, tiles: form.tiles })), 600);
    return () => clearTimeout(t);
  }, [form.areaIds, form.tiles]);
  const preview = useQuery({
    queryKey: ['monitors', 'preview', previewKey],
    queryFn: () => api<{ data: Record<string, TileData> }>('/monitors/preview', { method: 'POST', body: JSON.parse(previewKey) }),
    placeholderData: (prev) => prev,
  });

  const previewDark = form.theme === 'dark' || (form.theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches) || (form.theme === 'schedule' && scheduledDark(new Date(), form.darkFrom, form.darkUntil));

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, allowedNetworks: networks.split(/[\s,;]+/).filter(Boolean) };
      return edit.id ? api<Monitor>(`/monitors/${edit.id}`, { method: 'PUT', body }) : api<Monitor>('/monitors', { method: 'POST', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monitors'] });
      toast.success('Statusmonitor gespeichert');
      onClose();
    },
    onError,
  });

  return (
    <div className="grid gap-4">
      <PageHeader
        title={edit.id ? `Statusmonitor „${edit.form.name}“` : 'Neuer Statusmonitor'}
        description="Kacheln zusammenstellen; die Vorschau zeigt die aktuellen Daten."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!form.name.trim()}>
              Speichern
            </Button>
          </div>
        }
      />
      <div className="grid gap-4 xl:grid-cols-[28rem_minmax(0,1fr)] 3xl:grid-cols-[32rem_minmax(0,1fr)]">
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Einstellungen</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <Field label="Name" htmlFor="mon-name">
                <Input id="mon-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="z. B. Werkstatt" />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Spalten" htmlFor="mon-cols">
                  <Select id="mon-cols" value={form.columns} onChange={(e) => set('columns', Number(e.target.value))}>
                    {[2, 3, 4, 5, 6].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Aktualisieren" htmlFor="mon-refresh">
                  <Select id="mon-refresh" value={form.refreshSeconds} onChange={(e) => set('refreshSeconds', Number(e.target.value))}>
                    {[15, 30, 60, 120, 300].map((n) => (
                      <option key={n} value={n}>
                        {n < 60 ? `${n} s` : `${n / 60} min`}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Darstellung" htmlFor="mon-theme">
                  <Select id="mon-theme" value={form.theme} onChange={(e) => set('theme', e.target.value as MonitorInput['theme'])}>
                    <option value="dark">dunkel</option>
                    <option value="light">hell</option>
                    <option value="auto">wie Anzeigegerät</option>
                    <option value="schedule">zeitgesteuert</option>
                  </Select>
                </Field>
              </div>
              {form.theme === 'schedule' && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  Dunkel von
                  <Input type="time" value={form.darkFrom} onChange={(e) => set('darkFrom', e.target.value)} aria-label="Dunkel ab" className="h-8 w-28" />
                  bis
                  <Input type="time" value={form.darkUntil} onChange={(e) => set('darkUntil', e.target.value)} aria-label="Dunkel bis" className="h-8 w-28" />
                  <span className="text-xs text-muted-foreground">(Uhrzeit des Anzeigegeräts)</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Am Bildschirm lässt sich die Darstellung zusätzlich über das Sonne/Mond-Symbol umschalten (gilt nur für dieses Gerät).</p>
              <fieldset className="grid gap-1.5 text-sm">
                <legend className="mb-1 font-medium">Ticketbereiche</legend>
                <p className="text-xs text-muted-foreground">Ticketkacheln zeigen nur diese Bereiche; ohne Auswahl alle.</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {areas.map((a) => (
                    <label key={a.id} className="flex items-center gap-2">
                      <Checkbox checked={form.areaIds.includes(a.id)} onCheckedChange={(v) => set('areaIds', v ? [...form.areaIds, a.id] : form.areaIds.filter((x) => x !== a.id))} />
                      {a.name}
                    </label>
                  ))}
                </div>
              </fieldset>
              <Field label="Nur aus diesen Netzen abrufbar" htmlFor="mon-nets" hint="CIDR, z. B. 192.168.10.0/24 – leer = überall mit der URL. Empfohlen für Bildschirme im Firmennetz.">
                <Input id="mon-nets" value={networks} onChange={(e) => setNetworks(e.target.value)} placeholder="192.168.10.0/24" />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} /> Aktiv (URL abrufbar)
              </label>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Kacheln</CardTitle>
              <CardDescription>Reihenfolge von links oben nach rechts unten; freie Felder werden automatisch aufgefüllt.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2">
              <div className="flex gap-2">
                <Select value={adding} onChange={(e) => setAdding(e.target.value as MonitorTileKey | '')} aria-label="Kachel auswählen">
                  <option value="">Kachel hinzufügen …</option>
                  {GROUPS.map((g) => (
                    <optgroup key={g} label={g}>
                      {MONITOR_TILE_KEYS.filter((k) => MONITOR_TILES[k].group === g).map((k) => (
                        <option key={k} value={k}>
                          {MONITOR_TILES[k].label} – {MONITOR_TILES[k].description}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!adding}
                  onClick={() => {
                    if (!adding) return;
                    const d = MONITOR_TILES[adding];
                    setTiles((t) => [...t, { id: newId(), key: adding, w: Math.min(d.defaultW, form.columns), h: d.defaultH, options: {} }]);
                    setAdding('');
                  }}
                >
                  <Plus /> Hinzufügen
                </Button>
              </div>
              {form.tiles.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Kacheln.</p>}
              {form.tiles.map((t, i) => (
                <TileRow
                  key={t.id}
                  tile={t}
                  index={i}
                  count={form.tiles.length}
                  columns={form.columns}
                  onChange={(nt) => setTiles((all) => all.map((x) => (x.id === t.id ? nt : x)))}
                  onMove={(d) =>
                    setTiles((all) => {
                      const next = [...all];
                      [next[i], next[i + d]] = [next[i + d]!, next[i]!];
                      return next;
                    })
                  }
                  onRemove={() => setTiles((all) => all.filter((x) => x.id !== t.id))}
                />
              ))}
            </CardContent>
          </Card>
        </div>
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Vorschau</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn('aspect-video overflow-hidden rounded-lg border bg-background p-2 text-foreground', previewDark ? 'dark' : '')}>
              <MonitorGrid columns={form.columns} tiles={form.tiles} data={preview.data?.data ?? {}} className="h-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function MonitorsPage() {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const [edit, setEdit] = useState<Edit | null>(null);
  const { data } = useQuery({ queryKey: ['monitors'], queryFn: () => api<{ items: Monitor[] }>('/monitors') });
  const { data: areas } = useQuery({ queryKey: ['ticket-config', 'areas'], queryFn: () => api<AreaConfig[]>('/ticket-config/areas') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['monitors'] });
  const rotate = useMutation({ mutationFn: (id: string) => api(`/monitors/${id}/rotate`, { method: 'POST' }), onSuccess: () => (refresh(), toast.success('Neue URL erzeugt – bitte auf den Bildschirmen neu öffnen')), onError });
  const remove = useMutation({ mutationFn: (id: string) => api(`/monitors/${id}`, { method: 'DELETE' }), onSuccess: refresh, onError });
  const items = useMemo(() => data?.items ?? [], [data]);

  if (edit) return <Editor edit={edit} areas={areas ?? []} onClose={() => setEdit(null)} />;

  const copy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(urlOf(token));
      toast.success('URL kopiert');
    } catch {
      toast.error('Kopieren nicht möglich – bitte die URL markieren und kopieren');
    }
  };

  return (
    <div className="grid gap-4">
      {confirmDialog}
      <PageHeader
        title="Statusmonitore"
        description="Wandanzeigen für Werkstatt und Büro – ohne Anmeldung über eine geheime URL, z. B. im Kiosk-Modus eines Fernsehers oder Tablets."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEdit({ id: null, form: emptyForm('workshop'), networks: '' })}>
              <Plus /> Vorlage Werkstatt
            </Button>
            <Button variant="outline" onClick={() => setEdit({ id: null, form: emptyForm('office'), networks: '' })}>
              <Plus /> Vorlage Büro
            </Button>
            <Button variant="outline" onClick={() => setEdit({ id: null, form: emptyForm('management'), networks: '' })}>
              <Plus /> Vorlage Leitung
            </Button>
            <Button onClick={() => setEdit({ id: null, form: emptyForm(), networks: '' })}>
              <Plus /> Leerer Monitor
            </Button>
          </div>
        }
      />
      {items.length === 0 && (
        <Card>
          <CardContent className="pt-5 text-sm text-muted-foreground">Noch kein Statusmonitor eingerichtet. Starten Sie mit einer Vorlage.</CardContent>
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-2 3xl:grid-cols-3">
        {items.map((m) => (
          <Card key={m.id}>
            <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2">
                  <MonitorPlay className="size-4 text-muted-foreground" /> {m.name}
                  {!m.enabled && <Badge variant="outline">aus</Badge>}
                  {m.allowedNetworks.length > 0 && <Badge variant="outline">Netz beschränkt</Badge>}
                </CardTitle>
                <CardDescription>
                  {m.tiles.length} Kacheln · {m.columns} Spalten · alle {m.refreshSeconds < 60 ? `${m.refreshSeconds} s` : `${m.refreshSeconds / 60} min`} · zuletzt angezeigt: {formatDateTime(m.lastViewedAt)}
                </CardDescription>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={`${m.name} bearbeiten`}
                  onClick={() => setEdit({ id: m.id, form: { name: m.name, columns: m.columns, refreshSeconds: m.refreshSeconds, theme: m.theme, darkFrom: m.darkFrom, darkUntil: m.darkUntil, areaIds: m.areaIds, allowedNetworks: m.allowedNetworks, enabled: m.enabled, tiles: m.tiles }, networks: m.allowedNetworks.join(', ') })}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  aria-label={`${m.name} löschen`}
                  onClick={async () => (await confirm({ title: `Statusmonitor „${m.name}“ löschen?`, description: 'Die URL funktioniert danach nicht mehr.', confirmLabel: 'Löschen', destructive: true })) && remove.mutate(m.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-2">
              <div className="flex items-center gap-2">
                <Input readOnly value={urlOf(m.token)} onFocus={(e) => e.target.select()} aria-label="URL" className="h-8 font-mono text-xs" />
                <Button size="icon" variant="outline" className="size-8 shrink-0" onClick={() => copy(m.token)} aria-label="URL kopieren">
                  <Copy className="size-4" />
                </Button>
                <Button size="icon" variant="outline" className="size-8 shrink-0" asChild>
                  <a href={urlOf(m.token)} target="_blank" rel="noreferrer" aria-label="Öffnen">
                    <ExternalLink className="size-4" />
                  </a>
                </Button>
              </div>
              <div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () =>
                    (await confirm({ title: 'Neue URL erzeugen?', description: 'Die bisherige URL wird sofort ungültig; Bildschirme müssen die neue URL öffnen.', confirmLabel: 'Neue URL' })) && rotate.mutate(m.id)
                  }
                >
                  <RefreshCw /> Neue URL erzeugen
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
